/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { api } from '../apiSlice';
import history from '../../modules/history';

let Auth = null;
if (typeof window === 'object') {
  Auth = require('j-toker/src/j-toker');
}

const initialState = {
  initialized: false,
  userData: null,
  loggedIn: false,
  admin: false
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    userLoggedIn: (state, action) => {
      state.loggedIn = true;
      state.initialized = true;
      state.userData = action.payload;
      state.admin = action.payload?.admin || false;
    },

    userLoggedOut: (state) => {
      state.loggedIn = false;
      state.initialized = true;
      state.userData = null;
      state.admin = false;
    }
  }
});

export const {
  userLoggedIn,
  userLoggedOut
} = authSlice.actions;

export const authApi = api
  .enhanceEndpoints({ addTagTypes: ['Auth'] })
  .injectEndpoints({
    endpoints: build => ({

      userRegister: build.mutation({
        queryFn: async (data) => {
          if (!Auth) return { data: {} };

          try {
            const response = await Auth.emailSignUp({
              first_name: data.first_name,
              last_name: data.last_name,
              phone: data.phone,
              email: data.email,
              catering_user: data.catering_user,
              password: data.password,
              password_confirmation: data.password_confirmation
            });

            return { data: response };
          } catch (error) {
            return { error: { status: 'AUTH_ERROR', data: error, error: error.message || 'Registration failed' } };
          }
        },
        invalidatesTags: ['Auth']
      }),

      userLogin: build.mutation({
        queryFn: async (data, { dispatch }) => {
          if (!Auth) return { data: {} };

          try {
            const response = await Auth.emailSignIn(data);

            dispatch(userLoggedIn(response.data));

            if (response.data.admin) {
              history.push('/admin');
            } else if (response.data.catering_user) {
              history.push('/catering/dashboard');
            } else if (response.data.trucks && response.data.trucks[0]) {
              history.push('/vendor/truck/' + response.data.trucks[0].slug);
            } else {
              history.push('/vendor/truck/new');
            }
            return { data: response.data };
          } catch (error) {
            return { error: { status: 'AUTH_ERROR', data: error, error: error.message || 'Login failed' } };
          }
        },
        invalidatesTags: ['Auth']
      }),

      userOAuthLogin: build.mutation({
        queryFn: async (options, { dispatch }) => {
          if (!Auth) return { data: {} };

          try {
            const response = await Auth.oAuthSignIn(options);

            dispatch(userLoggedIn(response));

            if (response.admin) {
              history.push('/admin');
            } else if (response.catering_user) {
              history.push('/catering/dashboard');
            } else if (response.trucks && response.trucks[0]) {
              history.push('/vendor/truck/' + response.trucks[0].slug);
            } else {
              history.push('/vendor/truck/new');
            }
            return { data: response };
          } catch (error) {
            return { error: { status: 'AUTH_ERROR', data: error, error: error.message || 'OAuth login failed' } };
          }
        },
        invalidatesTags: ['Auth']
      }),

      userLogout: build.mutation({
        queryFn: async (_, { dispatch }) => {
          if (!Auth) return { data: {} };

          try {
            await Auth.signOut();
            dispatch(userLoggedOut());
            window.location.href = window.location.origin + '/login';
            return { data: {} };
          } catch (error) {
            return { error: { status: 'AUTH_ERROR', data: error, error: error.message || 'Logout failed' } };
          }
        },
        invalidatesTags: ['Auth']
      }),

      requestPasswordReset: build.mutation({
        queryFn: async (email) => {
          if (!Auth) return { data: {} };
          const response = await Auth.requestPasswordReset({ email });
          return { data: response };
        },
        invalidatesTags: ['Auth']
      }),

      changePassword: build.mutation({
        queryFn: async (data) => {
          if (!Auth) return { data: {} };

          try {
            const response = await Auth.updatePassword({
              password: data.password,
              password_confirmation: data.password_confirmation
            });
            return { data: response };
          } catch (error) {
            return { error: { status: 'AUTH_ERROR', data: error, error: error.message || 'Password change failed' } };
          }
        },
        invalidatesTags: ['Auth']
      }),

    }),
  });

export const {
  useUserRegisterMutation,
  useUserLoginMutation,
  useUserOAuthLoginMutation,
  useUserLogoutMutation,
  useRequestPasswordResetMutation,
  useChangePasswordMutation,
} = authApi;

export default authSlice.reducer;
